import { LOCALE_ID, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppComponent } from './app.component';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as fromUserStore from '@nx-ratenow/core/data-access-user';
import { CoreLayoutFeatureCustomLayoutModule } from '@nx-ratenow/core/layout/feature-custom-layout';
import * as fromStepper from '@nx-ratenow/shared/feature-stepper';
import { AppRoutingModule } from './app-routing.module';

import { DatePipe } from '@angular/common';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { APP_TYPE } from '@nx-ratenow/core/config';
import {
  NAVIGATION_FEATURE_KEY,
  NavigationEffects,
  initialStateNavigation,
  localStorageSyncReducer,
  navigationReducer
} from '@nx-ratenow/core/data-access-navigation';
import { ALERT_FEATURE_KEY, AlertEffects, initialStateAlert, reducerAlert } from '@nx-ratenow/core/feature-alerts/store';
import { CLUSTER_FEATURE_KEY, ClusterEffects, initialStateCluster, reducerCluster } from '@nx-ratenow/core/feature-clusters/store';
import {
  CONDITIONAL_FEATURE_KEY,
  ConditionalEffects,
  conditionalReducer,
  initialStateCondition
} from '@nx-ratenow/core/feature-conditional-system/store';
import { CoreFeatureNotificationModule } from '@nx-ratenow/core/feature-notifications';
import { HttpConfigInterceptor, HttpErrorInterceptor, JwtInterceptor, LanguageInterceptor, RetryInterceptor } from '@nx-ratenow/core/interceptors';
import { BENCHMARK_FEATURE_KEY, BenchmarkEffects, initialStateBenchmark, reducerBenchmark } from '@nx-ratenow/dashboard/benchmark/store';
import { CLIENTS_FEATURE_KEY, ClientsEffects, initialStateClient, reducer } from '@nx-ratenow/dashboard/client/store';
import { AplicationTypeName } from '@nx-ratenow/shared/enums';
import { SENDINGS_FEATURE_KEY, SendingsEffects, initialSendingsState, sendingsReducer } from '@nx-ratenow/shared/feature-sendings-details';

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    MatDatepickerModule,
    MatNativeDateModule,

    CoreFeatureNotificationModule,
    MtxNativeDatetimeModule,
    BrowserModule,

    HttpClientModule,
    BrowserAnimationsModule,

    MatDialogModule,

    CoreLayoutFeatureCustomLayoutModule,

    // STORE

    StoreModule.forRoot(
      {
        router: routerReducer
      },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false
        }
      }
    ),
    StoreDevtoolsModule.instrument(),
    EffectsModule.forRoot([]),

    // AUTH - USER

    StoreModule.forFeature(fromUserStore.AUTH_FEATURE_KEY, fromUserStore.reducer, {
      metaReducers: [fromUserStore.localStorageSyncReducer],
      initialState: fromUserStore.initialState
    }),

    EffectsModule.forFeature([fromUserStore.AuthEffects]),

    // STEPPER STORE

    StoreModule.forFeature(fromStepper.STEPPER_FEATURE_KEY, fromStepper.reducer, {
      initialState: fromStepper.initialState
    }),

    // CLIENTS

    StoreModule.forFeature(CLIENTS_FEATURE_KEY, reducer, {
      initialState: initialStateClient
    }),

    EffectsModule.forFeature([ClientsEffects]),

    // NAVIGATION

    StoreModule.forFeature(NAVIGATION_FEATURE_KEY, navigationReducer, {
      metaReducers: [localStorageSyncReducer],
      initialState: initialStateNavigation
    }),

    EffectsModule.forFeature([NavigationEffects]),

    // CONDITIONAL SYSTEM

    StoreModule.forFeature(CONDITIONAL_FEATURE_KEY, conditionalReducer, {
      initialState: initialStateCondition
    }),

    EffectsModule.forFeature([ConditionalEffects]),

    // BENCHMARK

    StoreModule.forFeature(BENCHMARK_FEATURE_KEY, reducerBenchmark, {
      initialState: initialStateBenchmark
    }),

    EffectsModule.forFeature([BenchmarkEffects]),

    // ALERTS
    StoreModule.forFeature(ALERT_FEATURE_KEY, reducerAlert, {
      initialState: initialStateAlert
    }),

    EffectsModule.forFeature([AlertEffects]),

    //CLUSTERS
    StoreModule.forFeature(CLUSTER_FEATURE_KEY, reducerCluster, {
      initialState: initialStateCluster
    }),

    EffectsModule.forFeature([ClusterEffects]),

    // SENDINGS
    StoreModule.forFeature(SENDINGS_FEATURE_KEY, sendingsReducer, {
      initialState: initialSendingsState
    }),
    EffectsModule.forFeature([SendingsEffects])
  ],
  providers: [
    MatDatepickerModule,
    provideZoneChangeDetection({ eventCoalescing: false }),
    DatePipe,
    {
      provide: LOCALE_ID,
      useValue: 'es'
    },
    {
      provide: APP_TYPE,
      useValue: AplicationTypeName.adminWeb
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
